"use client";

import Image from "next/image";
import { Media_Assets } from "@/lib/sovereign-core/media";
import {
  ParcelQ_Btn_Primary_Class,
  ParcelQ_Heading_Class,
  ParcelQ_Input_Class,
  ParcelQ_Label_Mono_Class,
} from "./parcelq-styles";

type Search_State_Props = {
  Query_Text: string;
  Query_OnChange: (Value: string) => void;
  Search_OnSubmit: () => void;
  Search_OnCancel?: () => void;
  Is_Loading?: boolean;
  Error_Message?: string | null;
  Correction_Message?: string | null;
};

export function Search_State({
  Query_Text,
  Query_OnChange,
  Search_OnSubmit,
  Search_OnCancel,
  Is_Loading = false,
  Error_Message = null,
  Correction_Message = null,
}: Search_State_Props) {
  const Form_HandleSubmit = (Event: React.FormEvent) => {
    Event.preventDefault();
    if (Is_Loading) {
      return;
    }
    Search_OnSubmit();
  };

  return (
    <section className="relative flex min-h-[62vh] flex-1 flex-col items-center justify-center px-6 py-16 pb-10 sm:py-20 sm:pb-12">
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-visible"
        aria-hidden
      >
        <Image
          src={Media_Assets.Logo}
          alt=""
          width={581}
          height={606}
          priority
          className="h-auto max-h-[min(62vh,36rem)] w-auto max-w-[min(78vw,34rem)] object-contain opacity-[0.14] select-none"
        />
      </div>

      <div className="relative z-10 w-full max-w-xl text-center">
        <p className={`mb-3 ${ParcelQ_Label_Mono_Class}`}>California parcels only</p>
        <h1
          className={`mb-3 text-2xl sm:text-3xl ${ParcelQ_Heading_Class}`}
          style={{ textShadow: "0 1px 18px rgba(6, 13, 24, 0.85)" }}
        >
          APN or Address
        </h1>
        <p className="mb-8 text-sm text-pq-fg-subtle">
          Look up a California assessor parcel by APN or street address.
        </p>

        <form onSubmit={Form_HandleSubmit} className="flex w-full flex-col gap-3 sm:flex-row sm:gap-0">
          <input
            type="text"
            value={Query_Text}
            onChange={(Event) => Query_OnChange(Event.target.value)}
            placeholder="176 Continente Ave, Brentwood, CA"
            className={`h-11 flex-1 sm:rounded-r-none ${ParcelQ_Input_Class}`}
            autoComplete="street-address"
            spellCheck
            autoCorrect="on"
            autoCapitalize="words"
            inputMode="text"
            aria-label="California APN or street address"
            disabled={Is_Loading}
          />
          {Is_Loading ? (
            <button
              type="button"
              onClick={Search_OnCancel}
              className={`h-11 shrink-0 px-8 sm:rounded-l-none ${ParcelQ_Btn_Primary_Class}`}
            >
              Looking up…
            </button>
          ) : (
            <button type="submit" className={`h-11 shrink-0 px-8 sm:rounded-l-none ${ParcelQ_Btn_Primary_Class}`}>
              Evaluate
            </button>
          )}
        </form>

        <p className={`mt-3 ${ParcelQ_Label_Mono_Class} text-pq-fg-subtle`}>
          CA only · street address or APN
        </p>

        {Correction_Message ? (
          <p className="mt-4 rounded-lg border border-amber-300 bg-pq-bg-soft px-4 py-3 text-sm text-amber-950">
            {Correction_Message}
          </p>
        ) : null}

        {Error_Message ? (
          <p
            className="mt-5 rounded-lg border border-red-400/30 bg-red-950/50 px-4 py-3 text-sm text-red-300"
            role="alert"
          >
            {Error_Message}
          </p>
        ) : null}
      </div>
    </section>
  );
}
